import { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Thumbs, Zoom } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/thumbs'
import 'swiper/css/zoom'

// antd
import { Menu, Modal, Popover, Row, theme, Tooltip, Typography } from 'antd'

// constant
import {
    fileTypes,
    pdfTypes,
    pptxTypes,
    svgTypes,
    videoTypes,
} from '../../../../constants/mediaTypes'

import { Icon } from '@iconify/react'
import dayjs from 'dayjs'

import { getBackendBaseUrl } from '../../../../helpers/backendUrl'

import './index.css'
import FilePreview from './filePreview'

const getFileType = (url) => {
    const src = String(url || '')
    if (src.startsWith('data:image/')) return 'image'
    if (src.startsWith('data:video/')) return 'video'
    const ext = src.split('.').pop()?.toLowerCase()?.split('?')[0] || ''
    if (svgTypes.includes(`.${ext}`)) return 'svg'
    if (fileTypes.includes(`.${ext}`)) return 'image'
    if (videoTypes.includes(`.${ext}`)) return 'video'
    if (pdfTypes.includes(`.${ext}`)) return 'pdf'
    if (pptxTypes.includes(`.${ext}`)) return 'pptx'
    return 'other'
}

const MediaViewer = ({
    mediaFiles,
    handleDelete,
    open: controlledOpen,
    onOpenChange,
    initialIndex = 0,
    showThumbnails = true,
}) => {
    const { Text } = Typography

    const { useToken } = theme
    const { token } = useToken()

    const isControlled = controlledOpen !== undefined
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const isOpen = isControlled ? Boolean(controlledOpen) : uncontrolledOpen
    const setIsOpen = (next) => {
        if (!isControlled) setUncontrolledOpen(next)
        onOpenChange?.(next)
    }
    const [activeIndex, setActiveIndex] = useState(initialIndex)
    const [openMenuIndex, setOpenMenuIndex] = useState(null)
    const [thumbsSwiper, setThumbsSwiper] = useState(null)
    const swiperRef = useRef(null)
    const prevRef = useRef(null)
    const nextRef = useRef(null)
    const containerRef = useRef(null)

    const getScrollParent = () => {
        let el = containerRef.current?.parentElement
        while (el) {
            const { overflow, overflowY } = window.getComputedStyle(el)
            if (/auto|scroll/.test(overflow) || /auto|scroll/.test(overflowY)) {
                return el
            }
            el = el.parentElement
        }
        return document.body
    }

    // useEffect(() => {
    //     const swiper = swiperRef.current
    //     const prev = prevRef.current
    //     const next = nextRef.current

    //     if (swiper && prev && next) {
    //         swiper.params.navigation.prevEl = prev
    //         swiper.params.navigation.nextEl = next
    //         swiper.navigation.destroy()
    //         swiper.navigation.init()
    //         swiper.navigation.update()
    //     }
    // }, [swiperRef.current])

    useEffect(() => {
        if (!isOpen) return
        setActiveIndex(initialIndex)
    }, [isOpen, initialIndex])

    useEffect(() => {
        if (isOpen && swiperRef.current) {
            swiperRef.current.slideTo(activeIndex)
        }
    }, [isOpen, activeIndex])

    useEffect(() => {
        if (!isOpen) return

        const handleKeyDown = (e) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return

            const tag = e.target?.tagName
            if (
                tag === 'INPUT' ||
                tag === 'TEXTAREA' ||
                tag === 'SELECT' ||
                e.target?.isContentEditable
            ) {
                return
            }

            const swiper = swiperRef.current
            if (!swiper) return

            e.preventDefault()
            if (e.key === 'ArrowLeft') {
                swiper.slidePrev()
            } else {
                swiper.slideNext()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isOpen])

    const preparedFiles = (mediaFiles || []).map((src) => ({
        src,
        type: getFileType(src),
    }))

    const handleOpen = (index) => {
        setActiveIndex(index)
        setIsOpen(true)
    }

    const getFileName = (url) => {
        const src = String(url || '')
        if (src.startsWith('data:image/')) return 'Image'
        if (src.startsWith('data:')) return 'File'
        const parts = decodeURIComponent(src.split('?')[0]).split('/')
        return parts[parts.length - 1] || 'File'
    }

    const getAttachmentDisplayMeta = (url) => {
        const rawName = getFileName(url)
        const stamped = rawName.match(/^(.*)_(\d{10,13})(\.[^.]+)$/)
        if (!stamped) {
            return { name: rawName, uploadedLabel: '' }
        }

        const stamp = stamped[2]
        const uploadedAt =
            stamp.length >= 13
                ? dayjs(Number(stamp))
                : dayjs.unix(Number(stamp))
        const uploadedLabel = uploadedAt.isValid()
            ? `Uploaded ${uploadedAt.format('MMM D, YYYY')} at ${uploadedAt.format('h:mma')}`
            : ''

        return {
            name: `${stamped[1]}${stamped[3]}`,
            uploadedLabel,
        }
    }

    const handleDownload = async (file) => {
        const fileName = getFileName(file?.src) || 'downloaded-file'
        try {
            const params = new URLSearchParams({ url: file?.src })
            if (fileName) params.set('filename', fileName)
            const downloadUrl = `${getBackendBaseUrl()}/aws/download?${params.toString()}`

            const response = await fetch(downloadUrl, {
                credentials: 'include',
                cache: 'no-store',
            })

            if (!response.ok) throw new Error('Network response was not ok')

            const blob = await response.blob()
            const blobUrl = window.URL.createObjectURL(blob)

            const a = document.createElement('a')
            a.href = blobUrl
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            a.remove()

            window.URL.revokeObjectURL(blobUrl)
        } catch (err) {
            console.error('Download failed:', err)
            window.open(file?.src, '_blank')
        }
    }

    const AttachmentActions = ({ onDownload, onDelete, onRequestApproval }) => {
        return (
            <Menu
                className="custom-menu-no-padding"
                style={{
                    padding: 0,
                    margin: 0,
                }}
                items={[
                    {
                        key: '1',
                        label: (
                            <div style={{ padding: 0 }}>
                                Download attachment
                            </div>
                        ),
                        onClick: (e) => {
                            e.domEvent.stopPropagation()
                            onDownload()
                        },
                    },
                    ...(onDelete
                        ? [
                              {
                                  key: '2',
                                  style: {
                                      color: token.colorPalette.textColor.error,
                                  },
                                  label: (
                                      <div style={{ padding: 0 }}>
                                          Delete attachment
                                      </div>
                                  ),
                                  onClick: (e) => {
                                      e.domEvent.stopPropagation()
                                      onDelete(e)
                                  },
                              },
                          ]
                        : []),
                ]}
            />
        )
    }

    return (
        <>
            {showThumbnails ? (
                <div
                    ref={containerRef}
                    className="mediaViewerThumbs"
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        maxWidth: '100%',
                        minWidth: 0,
                        width: 'fit-content',
                    }}
                >
                    {preparedFiles.map((file, index) => {
                        const isImage = file.type === 'image'
                        const attachmentMeta = getAttachmentDisplayMeta(
                            file?.src
                        )
                        return (
                            <Tooltip
                                key={index}
                                placement="top"
                                arrow
                                color="#2f3439"
                                mouseEnterDelay={0.2}
                                {...(openMenuIndex === index
                                    ? { open: false }
                                    : {})}
                                styles={{
                                    body: {
                                        borderRadius: 10,
                                        padding: '10px 12px',
                                        maxWidth: 260,
                                    },
                                }}
                                title={
                                    <div className="attachmentHoverTooltip">
                                        <div className="attachmentHoverTooltipName">
                                            {attachmentMeta.name}
                                        </div>
                                        {attachmentMeta.uploadedLabel ? (
                                            <>
                                                <div
                                                    className="attachmentHoverTooltipDivider"
                                                    aria-hidden
                                                />
                                                <div className="attachmentHoverTooltipDate">
                                                    {attachmentMeta.uploadedLabel}
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                }
                            >
                            <div
                                onClick={() => handleOpen(index)}
                                style={{ cursor: 'pointer' }}
                                className="uploadAttachmentTask"
                            >
                                <FilePreview
                                    file={file}
                                    index={index}
                                    token={token}
                                    handleDownload={handleDownload}
                                    hideNameTooltip
                                />
                                {isImage && (
                                    <div className="hoverLayerImageAttachment" />
                                )}
                                <div className={'hoverShowImageAttachment'}>
                                    <Popover
                                        open={openMenuIndex === index}
                                        onOpenChange={(open) =>
                                            setOpenMenuIndex(
                                                open ? index : null
                                            )
                                        }
                                        content={
                                            <AttachmentActions
                                                onDownload={() => {
                                                    handleDownload(file)
                                                    setOpenMenuIndex(null)
                                                }}
                                                onDelete={
                                                    handleDelete
                                                        ? () => {
                                                              setOpenMenuIndex(
                                                                  null
                                                              )
                                                              handleDelete(
                                                                  file.src
                                                              )
                                                          }
                                                        : undefined
                                                }
                                            />
                                        }
                                        trigger="click"
                                        placement="bottom"
                                        arrow={false}
                                        getPopupContainer={getScrollParent}
                                        styles={{
                                            root: {
                                                minWidth: 180,
                                            },
                                            body: {
                                                padding: 0,
                                                overflowY: 'hidden',
                                            },
                                        }}
                                    >
                                        <div
                                            onClick={(e) => e.stopPropagation()}
                                            className="icon-hover-wrapper"
                                        >
                                            <div className="icon-hover-target">
                                                <Icon
                                                    icon="uiw:down"
                                                    width={16}
                                                    height={16}
                                                    color={
                                                        isImage
                                                            ? '#fff'
                                                            : 'gray'
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </Popover>
                                </div>
                            </div>
                            </Tooltip>
                        )
                    })}
                </div>
            ) : null}

            <Modal
                open={isOpen}
                onCancel={(e) => {
                    e.stopPropagation()
                    setIsOpen(false)
                }}
                footer={null}
                closeIcon={null}
                centered
                width="100vw"
                style={{
                    top: 0,
                    padding: 0,
                    maxWidth: '100vw',
                }}
                styles={{
                    header: {
                        backgroundColor: '#252628',
                        padding: '10px 24px',
                        borderRadius: 0,
                        borderBottom: '1px solid #4b4b4b',
                    },
                    body: {
                        padding: 0,
                        height: 'calc(100vh - 55px)',
                        backgroundColor: '#252628',
                    },
                    content: {
                        padding: 0,
                        margin: 0,
                        height: '100vh',
                        backgroundColor: '#252628',
                        borderRadius: 0,
                    },
                    footer: {},
                }}
                title={
                    <Row align={'middle'} justify="space-between">
                        <Row>
                            <Text
                                style={{
                                    color: '#fff',
                                    fontWeight: 200,
                                }}
                            >
                                {getFileName(mediaFiles[activeIndex])}
                            </Text>
                        </Row>
                        <Row
                            justify="end"
                            align="middle"
                            style={{ columnGap: '15px' }}
                        >
                            <Row
                                align="middle"
                                onClick={() =>
                                    handleDownload(preparedFiles[activeIndex])
                                }
                                style={{
                                    cursor: 'pointer',
                                    color: '#fff',
                                    columnGap: 6,
                                }}
                            >
                                <Icon
                                    icon="akar-icons:download"
                                    height={18}
                                    width={18}
                                />
                                <Text
                                    style={{
                                        color: '#fff',
                                        fontWeight: 400,
                                    }}
                                >
                                    Download
                                </Text>
                            </Row>

                            <Tooltip title="Close" placement="top">
                                <Icon
                                    icon="akar-icons:cross"
                                    onClick={() => setIsOpen(false)}
                                    height={18}
                                    width={18}
                                    style={{
                                        cursor: 'pointer',
                                        color: '#fff',
                                    }}
                                />
                            </Tooltip>
                        </Row>
                    </Row>
                }
            >
                <div className="relative">
                    <div className="custom-swiper-buttons">
                        <div
                            ref={prevRef}
                            className="swiper-button-prev"
                            style={{ cursor: 'pointer' }}
                        >
                            <Icon icon="ph:arrow-left-bold" />
                        </div>
                        <div
                            ref={nextRef}
                            className="swiper-button-next"
                            style={{ cursor: 'pointer' }}
                        >
                            <Icon icon="ph:arrow-right-bold" />
                        </div>
                    </div>

                    <Swiper
                        style={{ height: '85vh' }}
                        modules={[Navigation, Thumbs, Zoom]}
                        navigation={{
                            nextEl: '.swiper-button-next',
                            prevEl: '.swiper-button-prev',
                        }}
                        zoom={{ maxRatio: 3 }}
                        thumbs={{ swiper: thumbsSwiper }}
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper

                            // Wait until DOM refs are available
                            setTimeout(() => {
                                if (
                                    prevRef.current &&
                                    nextRef.current &&
                                    swiperRef.current
                                ) {
                                    swiperRef.current.params.navigation.prevEl =
                                        prevRef.current
                                    swiperRef.current.params.navigation.nextEl =
                                        nextRef.current

                                    swiperRef.current.navigation.destroy()
                                    swiperRef.current.navigation.init()
                                    swiperRef.current.navigation.update()
                                }
                            }, 0)
                        }}
                        onSlideChange={(swiper) => {
                            setActiveIndex(swiper.activeIndex)
                        }}
                    >
                        {preparedFiles.map((file, index) => (
                            <SwiperSlide
                                key={index}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                {file.type === 'video' ? (
                                    <video
                                        src={file.src}
                                        controls
                                        style={{
                                            maxHeight: '80vh',
                                            maxWidth: '70%',
                                        }}
                                    />
                                ) : file.type === 'image' ? (
                                    <div className="swiper-zoom-container">
                                        <img
                                            src={file.src}
                                            alt={`slide-${index}`}
                                            style={{
                                                maxHeight: '80vh',
                                                maxWidth: '70%',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </div>
                                ) : file.type === 'pdf' ? (
                                    <iframe
                                        src={file.src}
                                        style={{
                                            width: '70%',
                                            height: '80vh',
                                            border: 'none',
                                        }}
                                        title={`pdf-${index}`}
                                    />
                                ) : file.type === 'pptx' ? (
                                    <iframe
                                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                                            file.src
                                        )}`}
                                        style={{
                                            width: '70%',
                                            height: '80vh',
                                            border: 'none',
                                        }}
                                        title={`pptx-${index}`}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            color: 'white',
                                            fontSize: 18,
                                            textAlign: 'center',
                                        }}
                                    >
                                        File type not previewable
                                    </div>
                                )}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>

                {/* Thumbnails in Modal */}
                {/* <Swiper
                    onSwiper={setThumbsSwiper}
                    spaceBetween={10}
                    slidesPerView={6}
                    watchSlidesProgress
                    style={{ padding: 10, backgroundColor: '#252628' }}
                >
                    {preparedFiles.map((file, index) => (
                        <SwiperSlide key={index} style={{ height: 80 }}>
                            {file.type === 'video' ? (
                                <video
                                    src={file.src}
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        objectFit: 'cover',
                                    }}
                                />
                            ) : file.type === 'image' ? (
                                <img
                                    src={file.src}
                                    style={{
                                        width: '80px',
                                        height: '30px',
                                        objectFit: 'cover',
                                    }}
                                    alt={file.src}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: '#333',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 12,
                                    }}
                                >
                                    {file.type.toUpperCase()}
                                </div>
                            )}
                        </SwiperSlide>
                    ))}
                </Swiper> */}
            </Modal>
        </>
    )
}

export default MediaViewer
